import { createRng } from '../core/rng';
import type { Vec3 } from '../geo/types';
import { spatialSound, type SpatialSound, type SoundEmitter } from './spatialAudio';

interface Note {
  readonly hz: number;
  readonly seconds: number;
  readonly weight?: number;
  readonly delay?: number;
  /** Seconds to rise to full level; a struck sound's quick rise where absent. */
  readonly attack?: number;
  readonly to?: number;
  readonly wave?: OscillatorType;
  readonly echo?: boolean;
}

interface Voice {
  readonly source: AudioScheduledSourceNode;
  readonly nodes: readonly AudioNode[];
  readonly emitter: SoundEmitter;
  readonly distance: GainNode;
  readonly pan: StereoPannerNode;
  readonly filter: BiquadFilterNode;
}

/** Shared noise and a bounded voice pool. Every scheduled voice owns and releases its nodes. */
export class AudioVoices {
  private readonly active = new Set<Voice>();
  private readonly noise: AudioBuffer;
  private readonly rng = createRng(1538);
  private position: Vec3 = [0, 0, 0];
  private yaw = 0;

  constructor(
    private readonly ctx: AudioContext,
    private readonly bus: AudioNode
  ) {
    this.noise = ctx.createBuffer(1, ctx.sampleRate * 4, ctx.sampleRate);
    const data = this.noise.getChannelData(0);
    for (let i = 0; i < data.length; i++) data[i] = this.rng.range(-1, 1);
  }

  private graph(
    source: AudioScheduledSourceNode,
    note: Note,
    sound: SpatialSound,
    extras: AudioNode[] = []
  ): Voice {
    const envelope = this.ctx.createGain(),
      distance = this.ctx.createGain();
    const filter = this.ctx.createBiquadFilter(),
      pan = this.ctx.createStereoPanner();
    filter.type = 'lowpass';
    const start = this.ctx.currentTime + (note.delay ?? 0),
      end = start + note.seconds;
    envelope.gain.setValueAtTime(0, start);
    envelope.gain.linearRampToValueAtTime(
      sound.emitter.level * (note.weight ?? 1),
      start + (note.attack ?? Math.min(0.018, note.seconds / 4))
    );
    envelope.gain.exponentialRampToValueAtTime(0.00001, end);
    source.connect(extras[0] ?? envelope);
    for (let i = 0; i < extras.length; i++) extras[i]?.connect(extras[i + 1] ?? envelope);
    envelope.connect(filter).connect(distance).connect(pan).connect(this.bus);
    const nodes: AudioNode[] = [envelope, filter, distance, pan, ...extras];
    if (note.echo) {
      const reflection = this.ctx.createDelay(0.5),
        quiet = this.ctx.createGain();
      reflection.delayTime.value = 0.13;
      quiet.gain.value = 0.13;
      distance.connect(reflection).connect(quiet).connect(pan);
      nodes.push(reflection, quiet);
    }
    const voice = { source, nodes, emitter: sound.emitter, distance, pan, filter };
    this.place(voice, spatialSound(sound.emitter, this.position, this.yaw), false);
    source.onended = () => {
      this.release(voice);
    };
    this.active.add(voice);
    source.stop(end + 0.2);
    return voice;
  }

  private release(voice: Voice): void {
    voice.source.onended = null;
    voice.source.disconnect();
    for (const node of voice.nodes) node.disconnect();
    this.active.delete(voice);
  }

  private place(voice: Voice, sound: SpatialSound, smooth: boolean): void {
    const volume = sound.emitter.level > 0 ? sound.volume / sound.emitter.level : 0;
    const values: readonly [AudioParam, number][] = [
      [voice.distance.gain, volume],
      [voice.pan.pan, sound.pan],
      [voice.filter.frequency, sound.cutoff],
    ];
    for (const [parameter, value] of values) {
      if (smooth) parameter.setTargetAtTime(value, this.ctx.currentTime, 0.06);
      else parameter.value = value;
    }
  }

  tone(note: Note, sound: SpatialSound): void {
    if (this.active.size >= 64) return;
    const source = this.ctx.createOscillator(),
      start = this.ctx.currentTime + (note.delay ?? 0);
    source.type = note.wave ?? 'sine';
    source.frequency.setValueAtTime(note.hz, start);
    source.frequency.exponentialRampToValueAtTime(note.to ?? note.hz, start + note.seconds * 0.7);
    source.start(start);
    this.graph(source, note, sound);
  }

  grain(note: Note, sound: SpatialSound, q = 0.8): void {
    if (this.active.size >= 64) return;
    const source = this.ctx.createBufferSource(),
      band = this.ctx.createBiquadFilter();
    source.buffer = this.noise;
    band.type = 'bandpass';
    band.frequency.value = note.hz;
    band.Q.value = q;
    const start = this.ctx.currentTime + (note.delay ?? 0);
    band.frequency.setValueAtTime(note.hz, start);
    if (note.to) band.frequency.exponentialRampToValueAtTime(note.to, start + note.seconds * 0.7);
    source.start(start, this.rng.range(0, 4 - note.seconds - 0.2));
    this.graph(source, note, sound, [band]);
  }

  update(position: Vec3, yaw: number): void {
    this.position = position;
    this.yaw = yaw;
    const placed = new Map<SoundEmitter, SpatialSound>();
    for (const voice of this.active) {
      const sound = placed.get(voice.emitter) ?? spatialSound(voice.emitter, position, yaw);
      placed.set(voice.emitter, sound);
      this.place(voice, sound, true);
    }
  }

  stop(): void {
    for (const voice of this.active) {
      voice.source.stop();
      this.release(voice);
    }
  }
}
